import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import ProtectedRoute from './components/ProtectedRoute';
import Navigation from './components/Navigation';

// Auth pages
import SignIn from './pages/SignIn';
import Register from './pages/Register';

// User pages
import Dashboard from './pages/user/Dashboard';
import Account from './pages/user/Account';
import Schedule from './pages/user/Schedule';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import FormBuilder from './pages/admin/FormBuilder';
import FormPreview from './pages/admin/FormPreview';
import EventCreator from './pages/admin/EventCreator';
import ScheduleConfigurator from './pages/admin/ScheduleConfigurator';

// Registration pages (public)
import RevieweeRegistration from './pages/registration/RevieweeRegistration';
import ReviewerRegistration from './pages/registration/ReviewerRegistration';
import RegistrationSuccess from './pages/registration/RegistrationSuccess';

// Reviewer pages
import ReviewerScheduleView from './pages/reviewer/ScheduleView';

// Layout wrapper for authenticated pages
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50">
    <Navigation />
    <main>{children}</main>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/signin" element={<SignIn />} />
          <Route path="/register" element={<Register />} />
          
          {/* Public registration routes */}
          <Route path="/register/reviewee/:eventId" element={<RevieweeRegistration />} />
          <Route path="/register/reviewer/:eventId" element={<ReviewerRegistration />} />
          <Route path="/register/success" element={<RegistrationSuccess />} />
          
          {/* Public reviewer schedule routes */}
          <Route path="/events/:eventId/schedule" element={<ReviewerScheduleView />} />
          
          {/* Protected user routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/account" element={
            <ProtectedRoute>
              <AppLayout>
                <Account />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/schedule" element={
            <ProtectedRoute>
              <AppLayout>
                <Schedule />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Protected admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <AdminDashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/events" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <AdminDashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/events/create" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <EventCreator />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/events/:eventId/edit" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <EventCreator />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/events/:eventId/schedule" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <ScheduleConfigurator />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/form-builder" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <FormBuilder />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/forms/:formId/builder" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <FormBuilder />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/forms/:formId/preview" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <FormPreview />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <AdminDashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/settings" element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <AdminDashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Redirect root to appropriate dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
